import { Directive, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';

export interface ISelectScrollEvent {
  select: MatSelect;
  scrollEvent: Event;
}

@Directive({
  selector: 'mat-select[selectScroll]'
})
export class MatSelectScrollDirective implements OnChanges, OnDestroy, OnInit {

  @Input() thresholdPercent = .9;
  @Input() isLoading = false;
  @Input() disableLoading = false;

  @Output('selectScroll') scroll = new EventEmitter<ISelectScrollEvent>();
  elePanel: HTMLInputElement = null;
  preScroll: number = 0;

  private _onDestroy = new Subject<void>();

  constructor(public matSelect: MatSelect) { }

  ngOnInit(): void {
    this.matSelect.openedChange.pipe(
      filter(opened => opened),
      tap(() => {
        // Wait for the panel to be attached
        Promise.resolve().then(() => {
          this.elePanel = this.matSelect.panel.nativeElement;
          this.elePanel.addEventListener('scroll', this.onScroll.bind(this));
        });
      }),
      takeUntil(this._onDestroy)
    ).subscribe();

    this.matSelect.openedChange.pipe(
      filter(opened => !opened),
      tap(() => {
        this.removeScrollEventListener();
        this.elePanel = null;
      }),
      takeUntil(this._onDestroy)
    ).subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isLoading && !changes.isLoading.currentValue && this.matSelect.panel) {
      const panel = this.matSelect.panel.nativeElement;
      panel.style.overflow = 'auto';
      setTimeout(() => {
        if (this.elePanel && this.preScroll) {
          this.elePanel.scrollTo({ top: this.preScroll, behavior: "smooth" });
          this.preScroll = 0;
        }
      }, 100);
    }
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
    this.removeScrollEventListener();
  }

  private removeScrollEventListener(): void {
    if (this.matSelect?.panel?.nativeElement) {
      this.matSelect.panel.nativeElement
        .removeEventListener('scroll', this.onScroll);
    }
  }

  private onScroll(event: Event): void {
    if (this.thresholdPercent === undefined) {
      this.scroll.next({ select: this.matSelect, scrollEvent: event });
    } else {
      let ele = (<HTMLInputElement>event.target);
      const threshold = this.thresholdPercent * 100 * ele.scrollHeight / 100;
      const current = ele.scrollTop + ele.clientHeight;

      if (current > threshold && !this.disableLoading) {
        this.scroll.next({ select: this.matSelect, scrollEvent: event });
        this.preScroll = ele.scrollTop || 0;
        ele.style.overflow = "hidden";
      }
    }
  }
}
